// src/components/admin/AdminBlogEdit.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/AppContext';

const LANGS = ['en','de','sk','hu','ro','pl','hr','nl'];
const CATEGORIES = ["Collector's Guide","Legal Guide","Hardware Guide","Brand Story","Education","News"];

export default function AdminBlogEdit() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { showToast, user } = useApp();
  const [activeLang, setActiveLang] = useState('en');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    slug:'', category:"Collector's Guide", cover_image:'',
    published:false, featured:false, read_time_minutes:5,
    title:{}, excerpt:{}, content:{},
  });

  useEffect(() => {
    if (!isNew) {
      supabase.from('blog_posts').select('*').eq('id', id).single()
        .then(({ data }) => { if (data) setForm(data); });
    }
  }, [id, isNew]);

  const set  = (f, v) => setForm(x => ({ ...x, [f]: v }));
  const setL = (f, l, v) => setForm(x => ({ ...x, [f]: { ...(x[f]||{}), [l]: v } }));

  const handleSave = async () => {
    if (!form.title?.en) { showToast('English title required', 'error'); return; }
    setSaving(true);
    const payload = {
      ...form,
      slug: form.slug || form.title.en.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,''),
      author_id: user?.id,
      updated_at: new Date().toISOString(),
    };
    const { error } = isNew
      ? await supabase.from('blog_posts').insert(payload)
      : await supabase.from('blog_posts').update(payload).eq('id', id);
    setSaving(false);
    if (error) { showToast(error.message, 'error'); return; }
    showToast(isNew ? 'Post created!' : 'Post saved!', 'success');
    navigate('/admin/blog');
  };

  return (
    <div className="page-container" style={{ padding:'32px 24px', maxWidth:900 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <h2 style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.8rem', letterSpacing:'.05em' }}>
          {isNew ? 'New Post' : 'Edit Post'}
        </h2>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
            <span style={{ fontSize:'.8rem', color:'#888' }}>Published</span>
            <div onClick={() => set('published', !form.published)} style={{ width:40, height:22, borderRadius:11, background: form.published?'#4cca74':'#333', position:'relative', transition:'background .2s' }}>
              <div style={{ position:'absolute', top:3, left: form.published?21:3, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left .2s' }} />
            </div>
          </label>
          <button className="btn-primary" style={{ padding:'9px 20px', fontSize:'.82rem' }} onClick={handleSave} disabled={saving}>
            {saving ? <div className="spinner" style={{ width:18, height:18, borderWidth:2 }} /> : isNew ? 'Create Post' : 'Save Post'}
          </button>
        </div>
      </div>

      {/* Lang tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, background:'#111', borderRadius:12, padding:4, flexWrap:'wrap' }}>
        {LANGS.map(l => (
          <button key={l} onClick={() => setActiveLang(l)} style={{ flex:'0 0 auto', padding:'7px 14px', border:'none', borderRadius:8, background: activeLang===l?'#4cca74':'none', color: activeLang===l?'#000':'#555', fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.78rem', fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 300px', gap:32, alignItems:'start' }}>
        {/* Main content */}
        <div>
          <div style={{ marginBottom:16 }}>
            <div className="input-label">Title ({activeLang.toUpperCase()}){activeLang==='en'?' *':''}</div>
            <input className="input-field" value={form.title?.[activeLang]||''} onChange={e => setL('title', activeLang, e.target.value)} />
          </div>
          <div style={{ marginBottom:16 }}>
            <div className="input-label">Excerpt ({activeLang.toUpperCase()})</div>
            <textarea className="input-field" rows={3} value={form.excerpt?.[activeLang]||''} onChange={e => setL('excerpt', activeLang, e.target.value)} style={{ resize:'vertical' }} />
          </div>
          <div>
            <div className="input-label">Content ({activeLang.toUpperCase()})</div>
            <textarea className="input-field" rows={16} value={form.content?.[activeLang]||''} onChange={e => setL('content', activeLang, e.target.value)} style={{ resize:'vertical', lineHeight:1.8 }} placeholder="Write your article here. Use blank lines between paragraphs." />
          </div>
        </div>

        {/* Settings sidebar */}
        <div style={{ background:'#111', borderRadius:16, border:'1px solid rgba(255,255,255,.07)', padding:20 }}>
          <div style={{ marginBottom:16 }}>
            <div className="input-label">Category</div>
            <select className="input-field" value={form.category} onChange={e => set('category', e.target.value)} style={{ appearance:'none' }}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ marginBottom:16 }}>
            <div className="input-label">Cover Image URL</div>
            <input className="input-field" value={form.cover_image||''} onChange={e => set('cover_image', e.target.value)} placeholder="https://…" />
            {form.cover_image && <img src={form.cover_image} alt="" style={{ width:'100%', borderRadius:8, marginTop:8, aspectRatio:'16/9', objectFit:'cover' }} />}
          </div>
          <div style={{ marginBottom:16 }}>
            <div className="input-label">Read time (minutes)</div>
            <input className="input-field" type="number" min="1" value={form.read_time_minutes} onChange={e => set('read_time_minutes', Number(e.target.value))} />
          </div>
          <div>
            <div className="input-label">Slug (auto-generated if blank)</div>
            <input className="input-field" value={form.slug||''} onChange={e => set('slug', e.target.value)} placeholder="auto-generated" />
          </div>
        </div>
      </div>
    </div>
  );
}
